import React from 'react';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import './assets/fonts/globalFont.css';
import { useEffect, useState, useContext } from 'react';
//
// Removed CacheBuster import
import packageFile from '../package.json';
import Loader from './components/common/loader/Loader';
import NoInternet from './components/ui/NoInternet';
import { GlobalAlert } from './components';
import AppRoutes from './routing/AppRoutes';
import checkInternetConnection from '../internetCheck';
import axiosInstance from './utils/axiosInstance';
import AuthContext from './context/authContext/AuthContext';
import UserContext from './context/userContext/UserContext';
import CustomerContext from './context/customerContext/CustomerContext';
import { useProductGroup } from './context/ProductGroupContext/ProductGroupContextProvider';
import { useTermsAndConditions } from './context/termsAndConditionContext/TermsAndConditionContextProvider';
import { useRole } from './context/roleContext/RoleContextProvider';
import { useService } from './context/serviceContext/ServiceContextProvider';
import { useAlternateProduct } from './context/alternateProductContext/AlternateProductContextProvider';
import { useProposal } from './context/proposalContext/ProposalContextProvider';
import { useProduct } from './context/productContext/ProductContextProvider';
import { useMarginCalculator } from './context/marginCalculatorContext/MarginCalculatorContextProvider';
import { useAccountForm } from './context/accountFormContext/AccountFormContextProvider';
import { useEstimatedArrival } from './context/estimatedArrival/EstimatedArrivalContextProvider';
import { useFiscalYearContext } from './context/FiscalYearContext/FiscalYearContextProvider';
import { useSalesFileContext } from './context/salesFileContext/SalesFileContextProvider';
import { useBiCustomer } from './context/biCustomerContext/BiCustomerContextProvider';
import { useDashboard } from './context/dashboardContext/DashboardContextProvider';
import { usePaymentReminder } from './context/paymentReminderContext/PaymentReminderContextProvider';
import { useBts } from './context/btsContext/BtsContextProvider';
import { useNavigate } from 'react-router-dom';
import { useMinMaxContext } from './context/minMaxContext/MinMaxContextProvider';
import { ChatProvider } from '@jaybel/chat-lib';
import { messaging } from './config/firebaseconfig';
import { onMessage } from 'firebase/messaging';
import { toast } from 'react-toastify';

function App() {
  const { version } = packageFile;
  const { isLoading: authLoading } = useContext(AuthContext);
  const { isLoading: userLoading } = useContext(UserContext);
  const { isLoading: customerLoading } = useContext(CustomerContext);
  const { isLoading: productGroupLoading } = useProductGroup();
  const { isLoading: termAndConditionLoading } = useTermsAndConditions();
  const { isLoading: roleLoading } = useRole();
  const { isLoading: serviceLoading } = useService();
  const { isLoading: alternateProductLoading } = useAlternateProduct();
  const { isLoading: productLoading } = useProduct();
  const { isLoading: accountFormLoading } = useAccountForm();
  const { isLoading: ETALoading } = useEstimatedArrival();
  const { isLoading: fiscalYearLoading } = useFiscalYearContext();
  const { isLoading: salesFileLoading } = useSalesFileContext();
  const { isLoading: BiCustomerLoading } = useBiCustomer();
  const { isLoading: BtsLoading } = useBts();
  const { isLoading: minMaxSettingsLoading } = useMinMaxContext();
  const {
    isLoading: proposalLoading,
    loadingText,
    downloadPercentage,
  } = useProposal();
  const {
    isLoading: marginCalculatorLoading,
    loadingText: loadingTextMarginCalculator,
    downloadPercentage: downloadPercentageMarginCalculator,
  } = useMarginCalculator();
  const { isLoading: NotesLoading } = useDashboard();
  const { isLoading: PaymentReminderLoading } = usePaymentReminder();

  const isLoading = [
    authLoading,
    userLoading,
    customerLoading,
    productGroupLoading,
    termAndConditionLoading,
    roleLoading,
    serviceLoading,
    alternateProductLoading,
    productLoading,
    proposalLoading,
    marginCalculatorLoading,
    loadingTextMarginCalculator,
    downloadPercentageMarginCalculator,
    ETALoading,
    fiscalYearLoading,
    accountFormLoading,
    BiCustomerLoading,
    salesFileLoading,
    PaymentReminderLoading,
    BtsLoading,
    NotesLoading,
    minMaxSettingsLoading,
  ].some(Boolean);

  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    checkInternetConnection();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for foreground Firebase Cloud Messages
  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('Foreground Message received. ', payload);
      // Display a toast using the notification payload
      if (payload.notification) {
         toast.info(`${payload.notification.title}: ${payload.notification.body}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
         });
      }
    });

    return () => {
       // Cleanup listener when App unmounts
       unsubscribe();
    };
  }, []);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const route = currentUrl.searchParams.get('route');
    const token = currentUrl.searchParams.get('token');
    if (route && token) {
      if (route === 'user-activation') navigate(`/user-activation/${token}`);
      else if (route === 'reset-password') navigate(`/reset-password/${token}`);
    }
  }, [navigate]);

   useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleSwMessage = (event) => {
      if (event.data && event.data.type === 'FCM_BACKGROUND_STATUS_UPDATE') {
        console.log('Background FCM STATUS_UPDATE relayed from SW:', event.data.data);
        window.dispatchEvent(new CustomEvent('btsStatusUpdated', { detail: event.data.data }));
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSwMessage);
    };
  }, []);

  if (!isOnline) return <NoInternet />;

  return (
    <ChatProvider
      authBaseUrl="/SmartProAPI"
      chatServiceUrl="/chatService"
      axios={axiosInstance}
    >
      {isLoading && (
        <Loader
          loadingText={loadingText || loadingTextMarginCalculator}
          downloadPercentage={
            downloadPercentage || downloadPercentageMarginCalculator
          }
        />
      )}
      <GlobalAlert />
      <AppRoutes />
    </ChatProvider>
  );
}

export default App;
